// src/components/Navbar.jsx
//
// Top navigation, redesigned for a quieter dashboard feel — text-based
// items with a thin underline for the active route, instead of bright
// pill buttons. Inspired by the chrome of tools like Linear and Vercel
// where the data is the main character, not the menu.
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import {
    FiDownloadCloud,
    FiEye,
    FiGrid,
    FiLogOut,
    FiMenu,
    FiMaximize,
    FiMonitor,
    FiPackage,
    FiSmartphone,
    FiUploadCloud,
    FiUsers,
    FiX,
} from 'react-icons/fi';
import { HeartPulse } from 'lucide-react';

// Single source of truth for the menu — used by both the desktop bar
// and the mobile drawer.
const NAV_ITEMS = [
    { to: '/dashboard',    label: 'Dashboard', Icon: FiGrid },
    { to: '/devices',      label: 'Devices',   Icon: FiMonitor },
    // lucide defaults to 24px; 1em matches the react-icons size of the other items.
    { to: '/health',       label: 'Health',    Icon: (p) => <HeartPulse size="1em" {...p} /> },
    { to: '/remote',       label: 'Remote',    Icon: FiEye },
    { to: '/groups',       label: 'Groups',    Icon: FiUsers },
    { to: '/apps',         label: 'Apps',      Icon: FiSmartphone },
    { to: '/update',       label: 'Update',    Icon: FiDownloadCloud },
    { to: '/ota',          label: 'OTA',       Icon: FiUploadCloud },
    { to: '/aosp-updater', label: 'AOSP',      Icon: FiPackage },
    { to: '/provisioning', label: 'Enroll',    Icon: FiMaximize },
];

export default function Navbar() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const auth = getAuth();

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <header className="bg-surface-nav backdrop-blur-md border-b border-white/[0.06] sticky top-0 z-50">
            <div className="w-full px-5">
                <div className="flex items-center justify-between h-14">
                    {/* Logo — small mark + wordmark with a single accent. */}
                    <div className="flex items-center gap-3">
                        <img
                            src={`${process.env.PUBLIC_URL}/logo.png`}
                            alt=""
                            className="h-7 w-7 object-contain"
                        />
                        <span className="text-sm font-semibold tracking-wide text-gray-100">
                            MUSICDJ <span className="text-brand">MDM</span>
                        </span>
                    </div>

                    {/* Desktop nav — pill-shaped buttons with a subtle
                        border. Active route fills with translucent orange
                        + brighter orange ring + orange icon, matching the
                        reference monitoring dashboard. */}
                    <nav className="hidden lg:flex items-center gap-2">
                        {NAV_ITEMS.map(({ to, label, Icon }) => (
                            <NavLink key={to} to={to} className="group">
                                {({ isActive }) => (
                                    <span
                                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                            isActive
                                                ? 'brand-pill'
                                                : 'bg-white/[0.02] border-white/[0.08] text-gray-300 group-hover:bg-white/[0.05] group-hover:border-white/[0.15]'
                                        }`}
                                    >
                                        <Icon
                                            className={
                                                isActive
                                                    ? 'text-brand-400'
                                                    : 'text-gray-500 group-hover:text-gray-300'
                                            }
                                        />
                                        {label}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={handleLogout}
                            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-red-400 transition-colors"
                            title="Sign out"
                        >
                            <FiLogOut /> Logout
                        </button>

                        <button
                            className="lg:hidden text-gray-300 hover:text-white p-2"
                            onClick={() => setIsMobileMenuOpen((open) => !open)}
                            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            {isMobileMenuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer — flatter row buttons; auto-closes on tap. */}
                {isMobileMenuOpen && (
                    <nav className="lg:hidden py-2 flex flex-col animate-slide-down">
                        {NAV_ITEMS.map(({ to, label, Icon }) => (
                            <NavLink
                                key={to}
                                to={to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? 'bg-white/5 text-white'
                                            : 'text-gray-400 hover:text-gray-100 hover:bg-white/5'
                                    }`
                                }
                            >
                                <Icon className="text-base" />
                                <span className="font-medium">{label}</span>
                            </NavLink>
                        ))}
                        <button
                            onClick={handleLogout}
                            className="mt-1 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-gray-400 hover:text-red-400 hover:bg-white/5 transition-colors"
                        >
                            <FiLogOut /> Logout
                        </button>
                    </nav>
                )}
            </div>
        </header>
    );
}
